import React, { useEffect, useMemo, useRef, useState } from 'react';
import i18n from '../../i18n';
import { createPortal } from 'react-dom';
import { Meteor } from 'meteor/meteor';
import { ComparisonLiveView } from './components/ComparisonLiveView';
import { ComparisonPerfView } from './components/ComparisonPerfView';
import { IntegrationsDrawer } from './components/IntegrationsDrawer';
import { DrillDown } from './components/DrillDown';
import { DrillLoadingOverlay } from './components/DrillLoadingOverlay';
import { DrillErrorOverlay } from './components/DrillErrorOverlay';
import { clearDrillFocus } from './components/drill/crux/useDrillFocus';
import { PerformanceDrillDown } from './components/PerformanceDrillDown';
import { GuestReviewsDrillDown } from './components/GuestReviewsDrillDown';
import { ExternalSignals } from './components/ExternalSignals';
import { FocusDrawer } from './components/FocusDrawer';
import { OpsSkeleton } from './components/OpsSkeleton';
import { PerformanceLoader } from './components/PerformanceLoader';
import { LiveUnavailable } from './components/LiveUnavailable';
import { ServiceRibbon } from './components/ServiceRibbon';
import { RecoverySection } from './components/RecoverySection';
import { OutletDeckDrawer } from './components/OutletDeckDrawer';
import { PerformanceView } from './components/PerformanceView';
import { MarketingConnectWizard } from './components/MarketingConnectWizard';
import { PosConnectModal } from './components/PosConnectModal';
import { isMarketingSourceId } from './data/marketingTypes';
import {
  DEFAULT_GROWTH_SUBVIEW,
  type GrowthDive,
  type GrowthFacet,
  type GrowthSubview,
} from './data/growthTypes';
import { maitreDemoNotice } from './data/growthDemo';
import { GROWTH_FLEET_PROPERTY } from './data/growthProperties';
import type { GrowthDemoChoices } from './components/growth/GrowthView';
import { isCurrentSWRData, isFailedSWREmpty, isMethodAbortError } from '../../lib/hooks';
import { SettingsHealthPanel } from './components/SettingsHealthPanel';
import { SubscriptionUsagePanel } from './components/SubscriptionUsagePanel';
import { SellItemsSection } from './components/SellItemsSection';
import { ScopeGroupsModal } from './components/ScopeGroupsModal';
import { MaitrePanel } from './components/maitre/MaitrePanel';
import { useNarrow } from './components/context/overlay';
import { ASK_NARROW_QUERY, askSurface } from './lib/askSurface';
import { MaitreProvider } from './components/maitre/MaitreContext';
import { useMaitreController } from './components/maitre/useMaitreController';
import { NorthStarDrillDown } from './components/NorthStarDrillDown';
import type { NorthStarNavTarget } from './lib/northStarFormat';
import { Topbar } from './components/Topbar';
import { NavRail } from './components/NavRail';
import { growthNavKey, useGrowthNav } from './components/growth/growthNavState';
import { Icon } from './components/shared/Icon';
import {
  loadNavRailExpanded,
  saveNavRailExpanded,
} from './lib/navRailPrefs';
import './styles/situation-brief.css';
import './styles/assistant.css';
import { buildUserSituationProfile } from './lib/userSituationProfile';
import type {
  AgentNavigateTarget,
  SituationCard,
  SituationEvidence,
} from './lib/situationTypes';
import { addCareAbout } from './lib/agentPrefs';
import {
  enrichSituationPack,
  mergeDiagnosisIntoPack,
} from './lib/enrichSituationPack';
import {
  drillPairFor,
  drillTabForKey,
  type DrillTabDef,
} from './components/shared/DrillMetricTabs';
import {
  normalizePerfDrillFromUrl,
  openPerfDrill,
  type PerfDrillState,
} from './lib/perfDrillNav';
import { ALWAYS_ON_SOURCES, SOURCES } from './data/sources';
import { diffNewBookings } from './lib/liveBookingToasts';
import { recoveryForOutlet } from './lib/liveRecovery';
import { sortLiveOutletOptions } from './lib/timelineOutletFilter';
import {
  expandScopeWithMappedRestaurants,
  isOutletFilter,
  migrateCurrentSelectionFilter,
  outletFilterKey,
  outletIdFromFilter,
  restaurantIdsForScopeFilter,
  scopeFilterLabel,
  scopeGroupIdFromFilter,
  scopeMembershipFingerprint,
  scopeOutletMembershipCount,
  setKnownScopeGroups,
} from './lib/scopeFilter';
import {
  compareItemsFromFilters,
  decodeOpsUrlState,
  getUrlBootstrap,
  type HotelTab,
} from './lib/opsUrlState';
import type { GrowthAgentScope } from './lib/growthPrompts';
import {
  useOpsCommandLive,
  useOpsComparison,
  useOpsDrillDown,
  useOpsForecast,
  useOpsMode,
  useOpsPerformance,
  useOpsEvents,
  useOpsPerformanceHeroNuggets,
  useOpsPerformanceDrillDown,
  useOpsOpportunityNarrative,
  useOpsSituationPack,
  useOpsSituationDiagnosis,
  perfDrillDataMatches,
  perfDrillIsPending,
  useOpsGuestReviews,
  useOpsRecovery,
  useOpsScopeCatalog,
  useOpsScopedOutletCount,
  useOpsSettingsAudit,
  useOpsSubscriptionUsage,
  useOpsConnectedPreferences,
  useOpsNorthStar,
  useOpsNorthStarDrillDown,
  useOpsScopeConfig,
  useOpsAccessibleRestaurants,
  pruneShiftsToCatalog,
} from './hooks/useOpsCommand';
import { useOpsUrlSync } from './hooks/useOpsUrlSync';
import { useViewContextStore, type SetContextOptions } from './hooks/useViewContext';
import { ViewContextProvider, type ViewContextValue } from './context/ViewContext';
import {
  buildViewContext,
  localToday,
  requestViewContext,
  resolveLinkVenues,
  screenCapabilities,
  screenIdFor,
  type ScreenId,
  type ViewContextLink,
  type ViewContextWindow,
} from './lib/viewContext';
import { setRequestViewContext } from '../../lib/requestContext';
import { useHorizon } from './components/horizon/useHorizon';
import { useHotelAvailability } from './components/hotel/useHotel';
import {
  normalizeWindowKey,
  outlookWindow,
  outlookWindows,
} from './components/horizon/horizonOutlookModel';
import type { FocusCrumb, OwnWindow } from './components/context/ContextBar';
import type { ComparisonItem } from './components/Topbar';
import type {
  AgentFocus,
  ConnectedSourceId,
  Outlet,
  OutletPerformanceRow,
  ScopeGroupSummary,
} from './types';
import { AgentAskProvider, useAgentAskContext } from './context/AgentAskContext';
import { INSIGHTS_AGENT_NAME } from './lib/agentBranding';
import { saveLocalLastScope } from '../../lib/clientConfig';
import { useScopeGroupsMove } from './hooks/useScopeGroupsMove';

import './styles/ops-command.css';
import './styles/nav-rail.css';
import './styles/topbar-filters.css';
import './styles/ops-command-extra.css';
import './styles/service-ribbon.css';
import './styles/guest-tags.css';
import './styles/marketing.css';
import './styles/typography.css';
import './styles/drill-crux.css';
import './styles/drill-deck.css';
import './styles/source-mix-inspector.css';
import './styles/ftue.css';
import {
  FtueOverlay,
  useFtueController,
  type FtueNavigateApi,
  type FtueSurfaceContext,
  type FtueUiSnapshot,
} from './ftue';

const GrowthView = React.lazy(() =>
  import('./components/growth/GrowthView').then(module => ({
    default: module.GrowthView,
  })),
);
const HorizonView = React.lazy(() =>
  import('./components/horizon/HorizonView').then(module => ({
    default: module.HorizonView,
  })),
);
const HotelView = React.lazy(() =>
  import('./components/hotel/HotelView').then(module => ({
    default: module.HotelView,
  })),
);
const MaitreHome = React.lazy(() =>
  import('./components/maitre/MaitreHome').then(module => ({
    default: module.MaitreHome,
  })),
);

/** Automatic corrections of the context (a gone group, an unknown shift): never a choice. */
const ADJUST: SetContextOptions = { adjust: true };

function isManualScopeGroup(g: ScopeGroupSummary): boolean {
  return (
    !g.id.startsWith('srcgrp:') &&
    !g.id.startsWith('builtin:') &&
    g.dataSource !== 'opentable'
  );
}

type OpsCommandProps = {
  restaurantId: string;
  restaurantName?: string;
  /** Switch the base restaurant (global restaurant switcher). */
  onSwitchRestaurant?: (restaurantId: string) => void;
  /**
   * Bootstrap region filter when URL share params did not supply one
   * (typically restored from localStorage last scope).
   */
  initialRegionFilter?: string | null;
  /** User id (or auth-disabled sentinel) for persisting last scope. */
  scopeOwnerId?: string | null;
};

export function OpsCommand(props: OpsCommandProps) {
  return (
    <AgentAskProvider>
      <OpsCommandInner {...props} />
    </AgentAskProvider>
  );
}

function OpsCommandInner({
  restaurantId,
  restaurantName,
  onSwitchRestaurant,
  initialRegionFilter,
  scopeOwnerId,
}: OpsCommandProps) {
  const urlBootstrap = useMemo(() => getUrlBootstrap(), []);
  const fromUrl = urlBootstrap.hasShareParams ? urlBootstrap.state : null;

  const [mode, setMode, setModeTransient] = useOpsMode(fromUrl?.mode);
  // One view context (Phase B step 1, docs/view-context.md): the venues,
  // period, comparison and filters every screen reads live in one store,
  // kept per person and customer (org), restored from a shared link without
  // overwriting what the person saved for that org.
  const scopeConfig = useOpsScopeConfig(restaurantId);
  const orgId = (!scopeConfig.isValidating && scopeConfig.data?.orgId) || null;
  const viewCtx = useViewContextStore({
    ownerId: scopeOwnerId ?? null,
    restaurantId,
    orgId,
    initialRegionFilter,
  });
  const { regionFilter, perfSettings, status, source, shifts } = viewCtx.state;
  const { setRegionFilter, setPerfSettings, setStatus, setSource, setShifts } = viewCtx;
  const [compareItems, setCompareItems] = useState<ComparisonItem[]>(() => {
    if (!fromUrl || fromUrl.compareFilters.length < 2) return [];
    return compareItemsFromFilters(fromUrl.compareFilters, []);
  });
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [marketingWizardSource, setMarketingWizardSource] =
    useState<ConnectedSourceId | null>(null);
  const [growthView, setGrowthView] = useState<GrowthSubview>(
    () => fromUrl?.growthView ?? DEFAULT_GROWTH_SUBVIEW,
  );
  const [growthDive, setGrowthDive] = useState<GrowthDive | null>(
    () => fromUrl?.growthDive ?? null,
  );
  const [growthFacet, setGrowthFacet] = useState<GrowthFacet | null>(
    () => fromUrl?.growthFacet ?? null,
  );
  const [growthChannel, setGrowthChannel] = useState<string | null>(
    () => fromUrl?.growthChannel ?? null,
  );
  const [growthModel, setGrowthModel] = useState<string | null>(() => fromUrl?.growthModel ?? null);
  // Growth's demo group (Linden Group, fictional data): only on purpose,
  // from the Data health card or `?demo=group`.
  const [growthDemo, setGrowthDemo] = useState<boolean>(() => Boolean(fromUrl?.growthDemo));
  // The demo group's local focus (one of its venues or brands): the context
  // bar's breadcrumb, like Live's outlet; the context's venues never change.
  const [growthDemoFocus, setGrowthDemoFocus] = useState<string>(GROWTH_FLEET_PROPERTY);
  const [growthDemoChoices, setGrowthDemoChoices] = useState<GrowthDemoChoices | null>(null);
  // The venues Growth shows, handed to maître d'AI as its scope (WP4.5).
  const [growthAgentScope, setGrowthAgentScope] = useState<GrowthAgentScope | null>(null);
  // The sidebar's Growth sections come from the connection status of the
  // venues in view, kept per venues, never from a section loading (growthNavState).
  const growthNav = useGrowthNav(growthNavKey(restaurantId, regionFilter, growthDemo), growthDemo);
  // Growth's Display menu and Notes button sit in the context bar's tools.
  const [growthToolsSlot, setGrowthToolsSlot] = useState<HTMLElement | null>(null);
  const [horizonDate, setHorizonDate] = useState<string | null>(
    () => fromUrl?.horizonDate ?? null,
  );
  const [horizonOutlet, setHorizonOutlet] = useState<string | null>(
    () => fromUrl?.horizonOutlet ?? null,
  );
  const [horizonWindow, setHorizonWindow] = useState<string | null>(
    () => fromUrl?.horizonWindow ?? null,
  );
  const [hotelView, setHotelView] = useState<HotelTab>(() => fromUrl?.hotelView ?? 'service');
  const [assistantPane, setAssistantPane] = useState<'notebook' | 'dashboard'>(
    () => (fromUrl?.assistantPane === 'dashboard' ? 'dashboard' : 'notebook'),
  );
  const [focusIntegrationSource, setFocusIntegrationSource] =
    useState<ConnectedSourceId | null>(null);
  const [focusOutlet, setFocusOutlet] = useState<Outlet | null>(null);
  const [drillKey, setDrillKey] = useState<string | null>(
    () => fromUrl?.drillKey ?? null,
  );
  const [perfDrill, setPerfDrill] = useState<PerfDrillState | null>(() =>
    normalizePerfDrillFromUrl(fromUrl?.perfDrill ?? null),
  );
  const [reviewsDrillOpen, setReviewsDrillOpen] = useState(false);
  const [reservationDrawerOpen, setReservationDrawerOpen] = useState(false);
  // True when a metric/reviews drill was opened *from* the North Star, so its
  // close returns to the North Star drill instead of the bare dashboard.
  const [perfDrillReturn, setPerfDrillReturn] = useState(false);
  const [perfOutlet, setPerfOutlet] = useState<OutletPerformanceRow | null>(null);
  // The outlet drawer opens on the metric the outlet table is sorted by.
  const [perfOutletMetric, setPerfOutletMetric] = useState<string | null>(null);
  const [outletDrill, setOutletDrill] = useState<{
    outletId: string;
    name: string;
    meta?: string;
  } | null>(null);
  const { toggle: toggleConnectedSource, apply: applyConnectedPreferences } =
    useOpsConnectedPreferences();
  const pendingFocusOutletId = fromUrl?.focusOutletId ?? null;
  const [connectToast, setConnectToast] = useState<string | null>(null);
  const [posConnectOpen, setPosConnectOpen] = useState(false);
  const [scopeSettingsOpen, setScopeSettingsOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const [agentLaneOpen, setAgentLaneOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    const stored = sessionStorage.getItem('aleno_ops_agent_lane');
    return stored !== '0';
  });
  const [navPrefExpanded, setNavPrefExpanded] = useState(loadNavRailExpanded);
  const [navForceCollapsed, setNavForceCollapsed] = useState(false);
  // Narrow, the panel is never a column beside the page (lib/askSurface).
  const askNarrow = useNarrow(ASK_NARROW_QUERY);
  // Agent lane/drawer takes horizontal space — keep the left nav icons-only while open.
  const { lane: showAskLane, drawer: showAskDrawer } = askSurface({
    agentOpen,
    agentLaneOpen,
    narrow: askNarrow,
    mode,
  });
  const agentSurfaceOpen = showAskLane || showAskDrawer;
  const navExpanded =
    navPrefExpanded && !navForceCollapsed && !agentSurfaceOpen;
  const opsRootRef = useRef<HTMLDivElement | null>(null);
  const [ftueAppRoot, setFtueAppRoot] = useState<HTMLDivElement | null>(null);
  const [ftuePortalHost, setFtuePortalHost] = useState<HTMLDivElement | null>(
    null,
  );
  const [northStarDrill, setNorthStarDrill] = useState(
    () => fromUrl?.northStarDrill ?? false,
  );
  const { busy: agentBusy } = useAgentAskContext();
  const prevAgentBusyRef = useRef(false);
  const agentWasOpenWhileBusyRef = useRef(false);
  const [agentToast, setAgentToast] = useState(false);

  useEffect(() => {
    saveNavRailExpanded(navPrefExpanded);
  }, [navPrefExpanded]);

  useEffect(() => {
    const root = opsRootRef.current;
    if (!root) return;
    const fit = () => setNavForceCollapsed(root.offsetWidth < 900);
    fit();
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(fit)
        : null;
    ro?.observe(root);
    return () => ro?.disconnect();
  }, []);

  useEffect(() => {
    sessionStorage.setItem('aleno_ops_agent_lane', agentLaneOpen ? '1' : '0');
  }, [agentLaneOpen]);

  useEffect(() => {
    if (mode === 'assistant') {
      setAgentLaneOpen(false);
      setAgentOpen(false);
    }
  }, [mode]);

  useEffect(() => {
    if (agentBusy && (agentOpen || agentLaneOpen)) {
      agentWasOpenWhileBusyRef.current = true;
    }
  }, [agentBusy, agentOpen, agentLaneOpen]);

  useEffect(() => {
    if (prevAgentBusyRef.current && !agentBusy) {
      if (agentWasOpenWhileBusyRef.current && !agentOpen && !agentLaneOpen) {
        setAgentToast(true);
      }
      agentWasOpenWhileBusyRef.current = false;
    }
    prevAgentBusyRef.current = agentBusy;
  }, [agentBusy, agentOpen, agentLaneOpen]);

  useEffect(() => {
    if (!agentToast) {
      return;
    }
    const timer = window.setTimeout(() => setAgentToast(false), 8000);
    return () => window.clearTimeout(timer);
  }, [agentToast]);

  const openAgentToastTarget = () => {
    setAgentToast(false);
    if (
      typeof window !== 'undefined' &&
      window.matchMedia(ASK_NARROW_QUERY).matches
    ) {
      setAgentOpen(true);
    } else {
      setAgentLaneOpen(true);
    }
  };

  const isComparing = compareItems.length >= 2;
  const regionArg = regionFilter === 'all' ? null : regionFilter;

  const scopeCatalog = useOpsScopeCatalog(restaurantId);
  const fleetRestaurants = scopeConfig.data?.restaurants;
  // Global switcher list (all restaurants for a superuser). Only fetched when a
  // switch handler is wired, to avoid the full-table read where it can't be used.
  const accessibleRestaurants = useOpsAccessibleRestaurants(!!onSwitchRestaurant);
  const accessibleRestaurantList = accessibleRestaurants.data?.restaurants ?? [];
  const outletCatalog = useMemo(() => {
    if (accessibleRestaurantList.length) return accessibleRestaurantList;
    return fleetRestaurants ?? [];
  }, [accessibleRestaurantList, fleetRestaurants]);
  // Distinguish "still loading" from "loaded empty" — `[]` is truthy for
  // optional chaining fallbacks and used to wipe restored outlet: filters.
  const outletCatalogReady =
    accessibleRestaurants.data != null ||
    (!onSwitchRestaurant && scopeConfig.data != null);
  const scopeGroupsReady = scopeConfig.data != null;
  const scopedCount = useOpsScopedOutletCount(restaurantId, regionArg);

  // Live's outlet focus is a local drill-down (audit B4): it reads one outlet
  // of the venues on screen and never rewrites the venues, nor the restaurant.
  const [liveFocusId, setLiveFocusId] = useState<string | null>(null);
  const liveRegionArg = liveFocusId ? outletFilterKey(liveFocusId) : regionArg;

  const live = useOpsCommandLive(
    restaurantId,
    liveRegionArg,
    source,
    status,
    perfSettings.capacityBasis,
    // Only Live tab (and its 60s poll). Do not keep getLive warm on
    // Performance/Settings — that leaked network refresh into other sections.
    mode === 'live',
    shifts,
  );

  // New-booking snackbars: seed on first settled Live snapshot for the current
  // scope, then toast id diffs on later 60s polls (non-PII). Generation +
  // isValidating avoid keepPreviousData races when scope/filters change.
  const [bookingToast, setBookingToast] = useState<string | null>(null);
  const bookingSeenRef = useRef<Set<string>>(new Set());
  const bookingGenRef = useRef(0);
  const bookingSeededGenRef = useRef(-1);
  const bookingDataRef = useRef<unknown>(null);
  useEffect(() => {
    bookingGenRef.current += 1;
    bookingSeenRef.current = new Set();
    // Clear so a cached SWR payload after leaving/re-entering Live can re-seed.
    bookingDataRef.current = null;
    setBookingToast(null);
  }, [
    mode,
    liveRegionArg,
    source,
    status,
    shifts,
    restaurantId,
    perfSettings.capacityBasis,
  ]);
  useEffect(() => {
    if (mode !== 'live' || !live.data) return;
    // Wait until the in-flight fetch for the new key settles so we don't seed
    // (or toast) against a keepPreviousData board from the prior scope.
    if (live.isValidating) return;
    const reservations = live.data.serviceTimeline?.reservations ?? [];
    const gen = bookingGenRef.current;
    if (bookingSeededGenRef.current !== gen) {
      // Same object identity as before the scope bump = still previous data.
      if (bookingDataRef.current === live.data) return;
      bookingSeenRef.current = new Set(
        reservations.map(r => r.id).filter(Boolean),
      );
      bookingSeededGenRef.current = gen;
      bookingDataRef.current = live.data;
      return;
    }
    if (bookingDataRef.current === live.data) return;
    bookingDataRef.current = live.data;
    const result = diffNewBookings(
      reservations,
      bookingSeenRef.current,
      true,
    );
    bookingSeenRef.current = result.nextSeen;
    if (result.toasts[0]) {
      setBookingToast(result.toasts[0].message);
    }
  }, [mode, live.data, live.isValidating]);
  // Track which Live args the painted board belongs to. keepPreviousData keeps
  // the prior restaurant on screen while a scope switch fetches — without this
  // we would never show the loader on restaurant / filter changes.
  const liveArgsKey = useMemo(
    () =>
      [
        restaurantId ?? '',
        liveRegionArg ?? '',
        JSON.stringify(source ?? null),
        JSON.stringify(status ?? null),
        perfSettings.capacityBasis ?? '',
      ].join('\0'),
    [restaurantId, liveRegionArg, source, status, perfSettings.capacityBasis],
  );
  const [liveSettledArgsKey, setLiveSettledArgsKey] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (mode !== 'live') return;
    if (live.isValidating) return;
    if (!live.data?.kpis) return;
    setLiveSettledArgsKey(liveArgsKey);
  }, [mode, live.isValidating, live.data, liveArgsKey]);

  useEffect(() => {
    if (!bookingToast) return;
    const timer = window.setTimeout(() => setBookingToast(null), 5000);
    return () => window.clearTimeout(timer);
  }, [bookingToast]);

  // Forecast and recovery now ride along in the live payload. The dedicated
  // methods only fire as a fallback when a (e.g. stale) live payload lacks the
  // bundled field, so the common path makes no extra round-trips on load.
  const forecast = useOpsForecast(
    restaurantId,
    liveRegionArg,
    mode === 'live' && !!live.data && !live.data.forecast,
    source,
    status,
    perfSettings.capacityBasis,
  );
  const recovery = useOpsRecovery(
    restaurantId,
    liveRegionArg,
    mode === 'live' && !!live.data && !live.data.recovery,
    source,
    status,
    perfSettings.capacityBasis,
  );

  const forecastData = forecast.data ?? live.data?.forecast ?? null;
  const recoveryData = recovery.data ?? live.data?.recovery ?? null;
  const serverConnected = scopeCatalog.data?.connected ??
    live.data?.connected ?? [
      'reservations',
      'tableplan',
    ] satisfies ConnectedSourceId[];

  const connected = useMemo(
    () => applyConnectedPreferences(serverConnected),
    [applyConnectedPreferences, serverConnected],
  );

  // The snapshot (cached per scope-signature) only carries fleet/derived groups.
  // User-owned custom groups can span fleets and are per-user, so they come from
  // the uncached scope config and get merged in for the menu.
  const scopeGroups = useMemo(() => {
    const base =
      scopeCatalog.data?.scopeGroups ?? live.data?.scopeGroups ?? [];
    const configGroups = scopeConfig.data?.groups ?? [];
    const known = new Set(base.map(g => g.id));
    const extra: ScopeGroupSummary[] = configGroups
      .filter(g => !known.has(g.id))
      .map(g => ({
        id: g.id,
        name: g.name,
        count: g.restaurantIds.length,
        occ: 0,
        alerts: 0,
      }));
    return extra.length ? [...base, ...extra] : base;
  }, [scopeCatalog.data, live.data, scopeConfig.data]);
  // Manual groups only — drop legacy derived/builtin/OpenTable auto-groups.
  const visibleScopeGroups = useMemo(
    () => scopeGroups.filter(isManualScopeGroup),
    [scopeGroups],
  );

  // Persist last operational scope for return visits (URL deep links win on boot).
  // Skip while catalogs needed to interpret the active filter are still loading,
  // so a transient 'all' wipe cannot overwrite a restored group:/outlet: scope.
  const ownsVenues = viewCtx.ownsPart('venues');
  useEffect(() => {
    if (!scopeOwnerId || !restaurantId) return;
    // A shared link's venues never replace the person's own until they
    // change them (or keep the link's context).
    if (!ownsVenues) return;
    if (isOutletFilter(regionFilter) && !outletCatalogReady) return;
    if (scopeGroupIdFromFilter(regionFilter) && !scopeGroupsReady) return;
    saveLocalLastScope(scopeOwnerId, {
      restaurantId,
      regionFilter: regionFilter ?? 'all',
    });
  }, [
    scopeOwnerId,
    restaurantId,
    regionFilter,
    outletCatalogReady,
    scopeGroupsReady,
    ownsVenues,
  ]);
  const scopeOverlay = useMemo(
    () => ({
      scopeGroups,
      scopeGroupConfigs: scopeConfig.data?.groups ?? [],
      fleetRestaurants: outletCatalog,
      connected,
    }),
    [scopeGroups, scopeConfig.data?.groups, outletCatalog, connected],
  );
  const performance = useOpsPerformance(
    restaurantId,
    perfSettings,
    regionArg,
    mode === 'performance',
    source,
    status,
    shifts,
  );
  // keepPreviousData must not count as ready — otherwise filter/scope changes
  // fire NS, diagnosis, and hero on top of a new cold scorecard.
  const scorecardCurrent = isCurrentSWRData(performance);
  const scorecardFailedEmpty = isFailedSWREmpty(performance);
  const eventsOverview = useOpsEvents(
    restaurantId,
    perfSettings,
    regionArg,
    mode === 'performance',
    source,
    status,
  );
  const performanceHeroNuggets = useOpsPerformanceHeroNuggets(
    restaurantId,
    perfSettings,
    regionArg,
    false,
    source,
    status,
    shifts,
  );
  const situationPack = useOpsSituationPack(
    restaurantId,
    mode,
    perfSettings,
    regionArg,
    // Canvas brief is hosted in the Performance hero; other modes use the lane.
    !isComparing && mode === 'performance',
    source,
    status,
    shifts,
  );
  const situationDiagnosis = useOpsSituationDiagnosis(
    restaurantId,
    perfSettings,
    regionArg,
    !isComparing && mode === 'performance' && scorecardCurrent,
    source,
    status,
    shifts,
  );
  const northStar = useOpsNorthStar(
    restaurantId,
    perfSettings,
    regionArg,
    mode === 'live' ||
      (mode === 'performance' && (scorecardCurrent || scorecardFailedEmpty)),
    source,
    status,
    shifts,
  );
  const northStarDrillDown = useOpsNorthStarDrillDown(
    restaurantId,
    northStarDrill,
    perfSettings,
    regionArg,
    source,
    status,
    shifts,
  );
  const handleNorthStarNav = (target: NorthStarNavTarget) => {
    setNorthStarDrill(false);
    setAgentOpen(false);
    switch (target.kind) {
      case 'metric':
        setMode('performance');
        setReviewsDrillOpen(false);
        setPerfDrillReturn(true);
        setPerfDrill(openPerfDrill(target.metricKey));
        break;
      case 'reviews':
        setMode('performance');
        setPerfDrill(null);
        setPerfDrillReturn(true);
        setReviewsDrillOpen(true);
        break;
      case 'settings':
        setMode('settings');
        break;
      default:
        setMode('performance');
        setPerfDrill(null);
        break;
    }
  };

  // Close a drill that was opened from the North Star, returning to it.
  const returnToNorthStar = () => {
    setPerfDrillReturn(false);
    setNorthStarDrill(true);
  };

  const settingsAudit = useOpsSettingsAudit(
    restaurantId,
    perfSettings,
    regionArg,
    mode === 'settings' && !isComparing,
  );
  const subscriptionUsage = useOpsSubscriptionUsage(
    restaurantId,
    regionArg,
    mode === 'settings' && !isComparing,
  );
  const comparison = useOpsComparison({
    restaurantId,
    compareItems:
      isComparing &&
      mode !== 'growth' &&
      mode !== 'horizon' &&
      mode !== 'hotel' &&
      mode !== 'assistant'
        ? compareItems
        : [],
    mode:
      mode === 'growth' || mode === 'horizon' || mode === 'hotel' || mode === 'assistant'
        ? 'performance'
        : mode,
    source,
    status,
    shifts,
    perfSettings,
    overlay: scopeOverlay,
    hasNorthStar: !!northStar.data,
  });

  const drillDown = useOpsDrillDown(
    restaurantId,
    drillKey,
    liveRegionArg,
    source,
    status,
    perfSettings.capacityBasis,
    shifts,
  );
  const activePerfPair = perfDrill ? drillPairFor(perfDrill.metricKey) : null;
  const activePerfTab = activePerfPair?.tab ?? null;
  const perfSecondaryKey = activePerfPair?.secondary ?? null;

  const perfDrillDown = useOpsPerformanceDrillDown(
    restaurantId,
    mode === 'performance' ? (perfDrill?.metricKey ?? null) : null,
    perfSettings,
    regionArg,
    perfDrill?.outletId ?? null,
    source,
    status,
    perfSecondaryKey,
    shifts,
  );
  const perfDrillPending = perfDrill ? perfDrillIsPending(perfDrillDown) : false;
  const perfDrillReady =
    !!perfDrill &&
    perfDrillDataMatches(perfDrillDown.data, perfDrill.metricKey) &&
    !perfDrillPending;
  // A failed request with nothing to show: the drill keeps its shell (close,
  // tabs) and says so with a retry, instead of vanishing.
  const perfDrillFailed =
    !!perfDrill &&
    !perfDrillReady &&
    !perfDrillPending &&
    perfDrillDown.error != null &&
    !isMethodAbortError(perfDrillDown.error);

  // Start as soon as the drill opens so AI/Redis work overlaps drill BQ.
  const opportunityNarrative = useOpsOpportunityNarrative(
    restaurantId,
    mode === 'performance' ? (perfDrill?.metricKey ?? null) : null,
    perfSettings,
    regionArg,
    mode === 'performance' && !!perfDrill,
    perfDrill?.outletId ?? null,
    source,
    status,
    shifts,
  );
  const opportunityForMetric =
    !!perfDrill &&
    opportunityNarrative.data?.metricKey === perfDrill.metricKey;
  const opportunityNarrativeLoading =
    !!perfDrill &&
    !opportunityForMetric &&
    (!!opportunityNarrative.isLoading || !!opportunityNarrative.isValidating);

  const horizon = useHorizon(
    restaurantId,
    regionArg,
    mode === 'horizon',
    shifts,
  );

  // Hotel only appears for a scope with a hotel (PMS stays); it stays in the
  // rail while open, so a scope change lands on its "no hotel" note.
  const hotelAvailability = useHotelAvailability(restaurantId, regionArg);
  const hotelAvailable = !!hotelAvailability.data?.available || mode === 'hotel';

  const guestReviews = useOpsGuestReviews(
    restaurantId,
    perfSettings,
    regionArg,
    mode === 'performance' &&
      !!performance.data &&
      connected.includes('reviews'),
  );

  const perfDrillReviewsAvailable =
    connected.includes('reviews') &&
    !!guestReviews.data?.enabled &&
    !!guestReviews.data?.fleet;

  const availableShifts =
    (mode === 'live'
      ? live.data?.availableShifts
      : mode === 'horizon'
        ? horizon.data?.availableShifts
        : performance.data?.availableShifts) ??
    scopedCount.data?.availableShifts;

  useEffect(() => {
    const next = pruneShiftsToCatalog(shifts, availableShifts);
    if (
      next.length !== shifts.length ||
      next.some((name, i) => name !== shifts[i])
    ) {
      setShifts(next, ADJUST);
    }
  }, [availableShifts, setShifts, shifts]);

  const outlets = live.data?.outlets ?? [];
  const kpis = live.data?.kpis;

  // Live is always one restaurant: picker in ServiceRibbon + Recovery/Sell/signals.
  const [liveOutletId, setLiveOutletId] = useState<string | null>(null);
  const liveOutletChoices = useMemo(
    () => sortLiveOutletOptions(outlets),
    [outlets],
  );
  // The person's own groups for this customer, kept on the server (decision 7.7.2).
  const scopeGroupsWithIds = useMemo(
    () => scopeConfig.data?.groups ?? [],
    [scopeConfig.data?.groups],
  );
  useEffect(() => {
    setKnownScopeGroups(scopeGroupsWithIds);
  }, [scopeGroupsWithIds]);
  // "All venues of <group>": the opened venue's group the person can open.
  const orgVenues = scopeConfig.data?.orgVenues ?? null;
  const orgVenueIds = orgVenues?.restaurantIds ?? null;
  const topbarScopeIds = useMemo(
    () =>
      restaurantIdsForScopeFilter(
        regionFilter,
        scopeGroupsWithIds,
        restaurantId,
        orgVenueIds,
      ),
    [regionFilter, scopeGroupsWithIds, restaurantId, orgVenueIds],
  );
  const scopeMembershipCount = useMemo(
    () =>
      scopeOutletMembershipCount({
        regionFilter,
        groups: scopeGroupsWithIds,
        groupSummaries: visibleScopeGroups,
        scopedOutletCount: scopedCount.data?.scopedOutletCount,
        activeRestaurantId: restaurantId,
        orgVenueIds,
      }),
    [
      regionFilter,
      scopeGroupsWithIds,
      visibleScopeGroups,
      scopedCount.data?.scopedOutletCount,
      restaurantId,
      orgVenueIds,
    ],
  );
  // The groups this browser kept move to the server once (decision 7.7.2).
  useScopeGroupsMove({
    restaurantId,
    ownerId: scopeOwnerId ?? null,
    ready: scopeGroupsReady,
    onMoved: () => scopeConfig.mutate(),
  });
  const growthScopeKey = topbarScopeIds.join('|');
  useEffect(() => {
    if (mode !== 'growth' || growthView !== 'website' || growthDive !== 'site') {
      setGrowthChannel(null);
    }
    if (!growthDive) setGrowthFacet(null);
  }, [mode, growthView, growthDive]);
  useEffect(() => {
    setGrowthChannel(null);
    setGrowthDive(null);
    setGrowthFacet(null);
  }, [growthScopeKey]);
  const expandMappedScope = (mappedIds: string[]) => {
    const next = expandScopeWithMappedRestaurants({
      mappedIds,
      regionFilter,
      groups: scopeGroupsWithIds,
      activeRestaurantId: restaurantId,
      orgVenueIds,
    });
    if (!next) return;
    setRegionFilter(next.regionFilter);
    if (
      next.switchRestaurantId &&
      onSwitchRestaurant &&
      next.switchRestaurantId !== restaurantId
    ) {
      onSwitchRestaurant(next.switchRestaurantId);
    }
    void scopeCatalog.mutate();
  };
  // A focus outside the venues on screen (they changed) is dropped.
  const topbarScopeKey = topbarScopeIds.join('|');
  useEffect(() => {
    const ids = topbarScopeKey ? topbarScopeKey.split('|') : [];
    setLiveFocusId(prev => (prev && ids.length > 1 && ids.includes(prev) ? prev : null));
  }, [topbarScopeKey]);

  // Live restaurant picker = the venues on screen (the context), whatever
  // outlet Live focuses.
  const livePickerOutlets = useMemo(() => {
    const liveById = new Map(outlets.map(o => [o.id, o]));
    const catalogById = new Map(outletCatalog.map(r => [r.id, r]));
    const ids = topbarScopeIds.length > 0 ? topbarScopeIds : outlets.map(o => o.id);
    const rows = ids
      .map(id => {
        const snap = liveById.get(id);
        const cat = catalogById.get(id);
        if (!snap && !cat) return null;
        return {
          id,
          name: snap?.name ?? cat?.name ?? id,
          city: snap?.city ?? cat?.city,
          region: snap?.region ?? cat?.region,
          closed: snap?.closed,
        };
      })
      .filter((r): r is NonNullable<typeof r> => !!r);
    return sortLiveOutletOptions(rows);
  }, [topbarScopeIds, outletCatalog, outlets]);
  useEffect(() => {
    if (mode !== 'live') return;
    setLiveOutletId(prev => {
      // Keep selection if it remains in the picker fleet.
      if (prev && livePickerOutlets.some(o => o.id === prev)) return prev;
      // Prefer a real snapshot outlet (has seats/tables) over a stale stub id.
      const real =
        (prev && outlets.find(o => o.id === prev)) ||
        (prev && liveOutletChoices.find(o => o.id === prev && (o.seats ?? 0) > 0)) ||
        null;
      if (real) return real.id;
      return (
        outlets[0]?.id ??
        liveOutletChoices.find(o => (o.seats ?? 0) > 0)?.id ??
        liveOutletChoices[0]?.id ??
        livePickerOutlets[0]?.id ??
        null
      );
    });
  }, [mode, liveOutletChoices, livePickerOutlets, outlets]);

  /**
   * Focus Live on one outlet of the venues on screen and refetch its full
   * service timeline. Fleet getLive timelines are reservation-capped (~800),
   * so client-only filtering often blanks the load curve / Gantt while outlet
   * KPIs still show. A local drill-down (audit B4): the venues, the
   * restaurant opened and every other screen stay as they were.
   */
  const focusLiveOutlet = (id: string) => {
    if (!id) return;
    if (!livePickerOutlets.some(o => o.id === id)) return;
    setLiveOutletId(id);
    setDrillKey(null);
    setFocusOutlet(null);
    if (topbarScopeIds.length > 1) {
      setLiveFocusId(id);
    } else {
      void live.mutate();
    }
  };

  const liveOutlet =
    outlets.find(o => o.id === liveOutletId) ?? outlets[0] ?? null;
  const liveOutlets = liveOutlet ? [liveOutlet] : [];
  const liveRecovery = useMemo(
    () => recoveryForOutlet(recoveryData, liveOutlet, connected),
    [recoveryData, liveOutlet, connected],
  );

  const currentRestaurantName =
    accessibleRestaurantList.find(r => r.id === restaurantId)?.name ??
    outlets.find(o => o.id === restaurantId)?.name ??
    restaurantName ??
    'Restaurant';
  const totalOutletCount =
    scopeCatalog.data?.totalOutletCount ??
    live.data?.totalOutletCount ??
    outlets.length;
  // Compare/Groups stay available whenever there's something to scope: the
  // current fleet has >1 outlet, or the user can reach >1 restaurant overall
  // (custom groups are cross-fleet, so they're manageable from any base — and
  // this survives the transient 0-count while a switched fleet is loading).
  const accessibleCount = accessibleRestaurants.data?.restaurants?.length ?? 0;
  const showScopeSettings = totalOutletCount > 1 || accessibleCount > 1;

  const topbarOutletCount = scopeMembershipCount;

  useOpsUrlSync({
    restaurantId,
    state: {
      mode,
      regionFilter,
      scopeVenueIds: topbarScopeIds,
      compareItems,
      source,
      status,
      shifts,
      perfSettings,
      drillKey,
      perfDrill,
      focusOutlet,
      northStarDrill,
      growthView,
      growthDive,
      growthFacet,
      growthChannel,
      growthModel,
      growthDemo,
      horizonDate,
      horizonOutlet,
      horizonWindow,
      hotelView,
      assistantPane,
      viewContextMarked: true,
    },
    scopeGroups: visibleScopeGroups,
    fleetRestaurants: outletCatalog,
    outletCatalogReady,
    scopeGroupsReady,
    outlets,
    setters: {
      // Dropping a scope the catalog no longer has is a correction.
      setRegionFilter: (r: string | null) => setRegionFilter(r, ADJUST),
      setCompareItems,
      setFocusOutlet,
    },
    pendingFocusOutletId,
  });

  const compareFiltersKey = compareItems.map(i => i.filter).join('|');
  useEffect(() => {
    const filters = compareFiltersKey ? compareFiltersKey.split('|') : [];
    if (filters.length < 2) return;
    const rebuilt = compareItemsFromFilters(
      filters,
      visibleScopeGroups,
      outletCatalog,
    );
    setCompareItems(prev => {
      if (prev.length !== rebuilt.length) return rebuilt;
      const changed = rebuilt.some(
        (r, i) => r.label !== prev[i]?.label || r.color !== prev[i]?.color,
      );
      return changed ? rebuilt : prev;
    });
  }, [compareFiltersKey, visibleScopeGroups, outletCatalog]);

  // Back and forward: opening another venue adds a history entry, and the
  // venues on screen go with it. The context is read from the URL only on
  // page load, so the entry's venues are applied here, after App has opened
  // its restaurant (the venues are saved with that restaurant).
  const [historyEntry, setHistoryEntry] = useState<{ search: string } | null>(null);
  useEffect(() => {
    const onPop = () => setHistoryEntry({ search: window.location.search });
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  useEffect(() => {
    if (!historyEntry) return;
    const entry = decodeOpsUrlState(historyEntry.search);
    if (entry.compareFilters.length >= 2) {
      setCompareItems(compareItemsFromFilters(entry.compareFilters, visibleScopeGroups, outletCatalog));
      return;
    }
    if (compareItems.length) setCompareItems([]);
    const venues = migrateCurrentSelectionFilter(entry.regionFilter ?? 'all');
    if (venues !== regionFilter) setRegionFilter(venues);
  }, [historyEntry]);

  useEffect(() => {
    if (connected.includes('opentable') || !regionFilter) {
      return;
    }
    const groupId = scopeGroupIdFromFilter(regionFilter);
    if (
      groupId &&
      scopeGroups.some(g => g.id === groupId && g.dataSource === 'opentable')
    ) {
      setRegionFilter('all', ADJUST);
    }
  }, [connected, regionFilter, scopeGroups, setRegionFilter]);

  useEffect(() => {
    const activeGroupId = scopeGroupIdFromFilter(regionFilter);
    if (
      activeGroupId &&
      // Wait for the person's group list before pruning: their groups only
      // arrive once the scope config loads.
      scopeConfig.data &&
      !scopeConfig.isValidating &&
      !scopeGroups.some(group => group.id === activeGroupId)
    ) {
      setRegionFilter('all', ADJUST);
    }
    if (
      isOutletFilter(regionFilter) &&
      outletCatalog.length > 0 &&
      // The base restaurant is always a valid single-restaurant selection.
      outletIdFromFilter(regionFilter) !== restaurantId &&
      !outletCatalog.some(r => `outlet:${r.id}` === regionFilter)
    ) {
      setRegionFilter('all', ADJUST);
    }
  }, [
    regionFilter,
    scopeGroups,
    outletCatalog,
    scopeConfig.data,
    scopeConfig.isValidating,
    restaurantId,
    setRegionFilter,
  ]);

  useEffect(() => {
    if (focusOutlet && !outlets.some(o => o.id === focusOutlet.id)) {
      setFocusOutlet(null);
    }
  }, [regionFilter, outlets, focusOutlet]);

  // The two drill-downs are mode-specific; closing one when the operator leaves
  // its tab avoids a stale overlay flashing on the other mode.
  useEffect(() => {
    if (mode !== 'performance' && perfDrill) setPerfDrill(null);
    if (mode !== 'performance' && reviewsDrillOpen) setReviewsDrillOpen(false);
    if (mode !== 'live' && drillKey) setDrillKey(null);
    if (mode !== 'performance') setPerfOutlet(null);
  }, [mode, perfDrill, reviewsDrillOpen, drillKey]);

  // Growth setup writes the same marketingWizardSource the Connect drawer
  // uses on Live/Performance. Clear only when leaving Growth so a leftover
  // Ads/GA setup does not open the drawer on the next mode. Do not clear
  // whenever the source is set — handleConnect on Live still needs it.
  const prevModeRef = useRef(mode);
  useEffect(() => {
    const leftGrowth = prevModeRef.current === 'growth' && mode !== 'growth';
    const leftHorizon = prevModeRef.current === 'horizon' && mode !== 'horizon';
    prevModeRef.current = mode;
    if (leftGrowth) setMarketingWizardSource(null);
    if (leftHorizon) {
      setHorizonDate(null);
      setHorizonOutlet(null);
    }
  }, [mode]);

  // The in-tab outlet focus is leftover from the previous fleet. Drop it
  // when Apply changes membership (including group:current-selection, whose
  // filter string does not change) so Horizon shows the newly selected set.
  const horizonScopeKey = `${regionFilter || 'all'}|${scopeMembershipFingerprint(regionFilter) || ''}`;
  const prevHorizonScopeKey = useRef(horizonScopeKey);
  useEffect(() => {
    if (prevHorizonScopeKey.current === horizonScopeKey) return;
    prevHorizonScopeKey.current = horizonScopeKey;
    setHorizonOutlet(null);
  }, [horizonScopeKey]);

  useEffect(() => {
    if (isComparing) {
      setDrillKey(null);
      setPerfDrill(null);
      setFocusOutlet(null);
    }
  }, [isComparing]);

  const scopeLabel =
    regionFilter && regionFilter !== 'all'
      ? scopeFilterLabel(regionFilter, scopeGroups, outletCatalog, undefined, orgVenues?.name ?? null)
      : undefined;

  // A shared link's venues (decision 7.7.2): once the person's groups and the
  // venues they can open are known, the link shows its venues as far as they
  // can open them (their own group when it names one of theirs), and the
  // context says what it asked for, showed and dropped until they change the
  // venues. The correction is never saved as their choice.
  const [linkFacts, setLinkFacts] = useState<ViewContextLink | null>(null);
  const linkResolvedRef = useRef(false);
  useEffect(() => {
    const link = viewCtx.link;
    if (!link || linkResolvedRef.current) return;
    if (!scopeGroupsReady || !outletCatalogReady) return;
    linkResolvedRef.current = true;
    const openable = accessibleRestaurants.data
      ? new Set([...accessibleRestaurantList.map(r => r.id), restaurantId])
      : null;
    const resolved = resolveLinkVenues({
      link,
      ownGroups: scopeGroupsWithIds,
      orgVenueIds,
      openable,
    });
    setLinkFacts(resolved.facts);
    if (resolved.regionFilter !== regionFilter) setRegionFilter(resolved.regionFilter, ADJUST);
    // Once per page load: the link's first resolution is the one it shows.
  }, [viewCtx.link, scopeGroupsReady, outletCatalogReady]);
  const venuesLink = viewCtx.ownsPart('venues') ? null : linkFacts;

  // A screen's local drill-down inside the venues (audit B4): Live's outlet
  // focus and Forecast's "Showing"; step 3 shows it as a breadcrumb.
  const focusId =
    mode === 'live'
      ? liveFocusId
      : mode === 'horizon' && horizonOutlet && topbarScopeIds.length > 1 && topbarScopeIds.includes(horizonOutlet)
        ? horizonOutlet
        : null;
  const focusLabel = focusId
    ? (outlets.find(o => o.id === focusId)?.name ?? outletCatalog.find(r => r.id === focusId)?.name ?? focusId)
    : '';

  // The context bar's breadcrumb (Phase B step 3): local drill-downs inside
  // the context, each with its way back. None of them changes the context.
  const focusCrumbs = useMemo<FocusCrumb[]>(() => {
    const crumbs: FocusCrumb[] = [];
    if (focusId && mode === 'live') {
      crumbs.push({ key: 'live', label: focusLabel, onClear: () => setLiveFocusId(null) });
    }
    if (focusId && mode === 'horizon') {
      crumbs.push({ key: 'horizon', label: focusLabel, onClear: () => setHorizonOutlet(null) });
    }
    if (mode === 'growth' && growthDemo && growthDemoFocus !== GROWTH_FLEET_PROPERTY) {
      const label =
        growthDemoChoices?.venues.find(v => v.id === growthDemoFocus)?.name ??
        growthDemoChoices?.brands.find(b => b.key === growthDemoFocus)?.name ??
        growthDemoFocus;
      crumbs.push({ key: 'growth-demo', label, onClear: () => setGrowthDemoFocus(GROWTH_FLEET_PROPERTY) });
    }
    if (mode === 'performance' && !isComparing) {
      if (northStarDrill) {
        crumbs.push({ key: 'ns', label: i18n.t('chrome:context.focus.northStar'), onClear: () => setNorthStarDrill(false) });
      }
      if (perfDrill) {
        crumbs.push({
          key: 'drill',
          label: drillTabForKey(perfDrill.metricKey)?.label ?? perfDrill.metricKey,
          onClear: () => {
            setOutletDrill(null);
            setPerfDrill(null);
          },
        });
        if (outletDrill) crumbs.push({ key: 'drill-outlet', label: outletDrill.name, onClear: () => setOutletDrill(null) });
      }
      if (reviewsDrillOpen) {
        crumbs.push({ key: 'reviews', label: i18n.t('chrome:context.focus.reviews'), onClear: () => setReviewsDrillOpen(false) });
      }
      if (perfOutlet) {
        crumbs.push({ key: 'outlet', label: perfOutlet.outlet.name, onClear: () => setPerfOutlet(null) });
      }
    }
    return crumbs;
  }, [
    focusId,
    focusLabel,
    mode,
    isComparing,
    northStarDrill,
    perfDrill,
    outletDrill,
    reviewsDrillOpen,
    perfOutlet,
    growthDemo,
    growthDemoFocus,
    growthDemoChoices,
  ]);

  // A screen's own window in the bar: Forecast's outlook (chosen there or in
  // the chip) and Live's today. The period set for other screens shows muted.
  const horizonOutlook = horizon.data?.outlook;
  const ownWindow = useMemo<OwnWindow | null>(() => {
    if (mode === 'live') return { kind: 'live', date: localToday() };
    if (mode !== 'horizon' || !horizonOutlook) return null;
    const scopeId = focusId && mode === 'horizon' ? focusId : null;
    const windows = outlookWindows(horizonOutlook, scopeId).length
      ? outlookWindows(horizonOutlook, scopeId)
      : outlookWindows(horizonOutlook, null);
    const key = normalizeWindowKey(horizonWindow);
    const current = windows.find(w => w.key === key) ?? outlookWindow(horizonOutlook, null, key);
    if (!current) return null;
    return {
      kind: 'forecast',
      label: current.label,
      start: current.start ?? null,
      end: current.end ?? null,
      options: windows.map(w => ({ key: w.key, label: w.label })),
      value: key,
      onChange: (next: string) => setHorizonWindow(next),
    };
  }, [mode, horizonOutlook, focusId, horizonWindow]);

  // The screen on display and its full context, sent with every request
  // (lib/requestContext.ts) and every maître d'AI question. Growth reads the
  // context's venues like every screen (decision 7.7.1); only the fictional
  // demo group keeps its own picker, so its context names the demo venues.
  const screen = screenIdFor({
    mode,
    growthView,
    drillOpen: !!perfDrill || northStarDrill || reviewsDrillOpen,
  });
  const growthVenues =
    mode === 'growth' && growthDemo && growthAgentScope?.venueIds?.length ? growthAgentScope : null;
  const venueIds = growthVenues?.venueIds ?? topbarScopeIds;
  const venueIdsKey = venueIds.join('|');
  const venueLabel = growthVenues?.label ?? scopeLabel ?? currentRestaurantName;
  const venueOrigin = growthVenues?.venueOrigin;
  const venueFilter = growthVenues ? growthVenues.regionFilter : regionFilter;
  // The screen's own window travels with the context (Phase C): a
  // conversation started on Forecast or Live reads that window.
  const windowKind = ownWindow?.kind ?? null;
  const windowStart = ownWindow ? (ownWindow.kind === 'live' ? ownWindow.date : ownWindow.start) : null;
  const windowEnd = ownWindow ? (ownWindow.kind === 'live' ? ownWindow.date : ownWindow.end) : null;
  const windowLabel = ownWindow?.kind === 'forecast' ? ownWindow.label : null;
  const windowKey = ownWindow?.kind === 'forecast' ? ownWindow.value : null;
  const screenWindow = useMemo<ViewContextWindow | null>(
    () =>
      windowKind
        ? { kind: windowKind, start: windowStart, end: windowEnd, label: windowLabel, key: windowKey }
        : null,
    [windowKind, windowStart, windowEnd, windowLabel, windowKey],
  );
  const requestContext = useMemo(
    () =>
      requestViewContext(
        buildViewContext(viewCtx.state, {
          screen,
          venues: {
            ids: venueIdsKey ? venueIdsKey.split('|') : [],
            label: venueLabel,
            origin: venueOrigin,
            orgId: viewCtx.orgId,
            filter: venueFilter,
            link: venuesLink,
          },
          focus: focusId ? { kind: 'outlet', id: focusId, label: focusLabel, screen } : null,
        }),
        screen,
        screenWindow,
      ),
    [
      screenWindow,
      viewCtx.state,
      viewCtx.orgId,
      screen,
      venueIdsKey,
      venueLabel,
      venueOrigin,
      venueFilter,
      venuesLink,
      focusId,
      focusLabel,
    ],
  );
  // "Your Google accounts" sits outside any restaurant's context (docs/19
  // section 5). maître d'AI there starts from the person's last restaurant
  // view on Growth and says so ("Your last restaurant view: …"), never from
  // the page itself (docs/view-context.md section 11).
  const onGoogleAccounts = screen === 'growth:accounts';
  const [lastRestaurantScreen, setLastRestaurantScreen] = useState<ScreenId>('growth:overview');
  useEffect(() => {
    if (screen.startsWith('growth:') && screen !== 'growth:accounts') setLastRestaurantScreen(screen);
  }, [screen]);
  const maitreContext = useMemo(
    () =>
      onGoogleAccounts
        ? requestViewContext(
            buildViewContext(viewCtx.state, {
              screen: lastRestaurantScreen,
              venues: {
                ids: venueIdsKey ? venueIdsKey.split('|') : [],
                label: venueLabel,
                origin: venueOrigin,
                orgId: viewCtx.orgId,
                filter: venueFilter,
                link: venuesLink,
              },
              focus: null,
            }),
            lastRestaurantScreen,
            null,
          )
        : requestContext,
    [
      onGoogleAccounts,
      lastRestaurantScreen,
      requestContext,
      viewCtx.state,
      viewCtx.orgId,
      venueIdsKey,
      venueLabel,
      venueOrigin,
      venueFilter,
      venuesLink,
    ],
  );
  // The include filters this screen applies; the Topbar names only these as
  // applying, and keeps the others settable for the screens that use them.
  const screenUses = screenCapabilities(screen).uses;
  const filterUses = useMemo(
    () => ({ statuses: screenUses.statuses, sources: screenUses.sources, shifts: screenUses.shifts }),
    [screenUses],
  );
  // Growth's analysis sections apply them too (decision 7.5.2); the others
  // (campaigns, setup, Data health) send none.
  const growthFilters = useMemo(
    () => ({
      status: filterUses.statuses ? (status as string[]) : [],
      source: filterUses.sources ? (source as string[]) : [],
      shifts: filterUses.shifts ? shifts : [],
    }),
    [filterUses, status, source, shifts],
  );
  // Set while rendering, so the requests this render starts already carry it.
  setRequestViewContext(requestContext);
  useEffect(() => () => setRequestViewContext(null), []);
  const viewContextValue = useMemo<ViewContextValue>(
    () => ({ store: viewCtx, screen, request: requestContext }),
    [viewCtx, screen, requestContext],
  );

  const handleConnect = (needs: ConnectedSourceId) => {
    if (needs === 'pos') {
      setPosConnectOpen(true);
      return;
    }
    if (isMarketingSourceId(needs)) {
      setMarketingWizardSource(needs);
      return;
    }
    // Weather / events: server already offers them — one-click enable (opt-out
    // sources start disconnected until the user connects).
    if (
      (needs === 'weather' || needs === 'events') &&
      serverConnected.includes(needs) &&
      !connected.includes(needs)
    ) {
      toggleConnectedSource(needs);
      return;
    }
    setFocusIntegrationSource(needs);
    setIntegrationsOpen(true);
    if (!serverConnected.includes(needs)) {
      const label = SOURCES.find(s => s.id === needs)?.name ?? needs;
      const message =
        needs === 'demandgen'
          ? 'Demandgen is not available for this scope. Enable demandgen.enabled in backend settings and ensure an audit is mapped to the outlet.'
          : `${label} integration is not configured. Contact your account manager to connect this data source.`;
      setConnectToast(message);
      setTimeout(() => setConnectToast(null), 5000);
    }
  };

  const handleOpenIntegrations = () => {
    setFocusIntegrationSource(null);
    setIntegrationsOpen(true);
  };

  const handleCloseIntegrations = () => {
    setIntegrationsOpen(false);
    setFocusIntegrationSource(null);
  };

  const handleToggleSource = async (id: ConnectedSourceId) => {
    const meta = SOURCES.find(s => s.id === id);
    if (meta?.core || meta?.alwaysOn || ALWAYS_ON_SOURCES.includes(id)) {
      if (id === 'pos' && !serverConnected.includes('pos')) {
        setPosConnectOpen(true);
      }
      return;
    }

    const isOn = connected.includes(id);

    if (id === 'opentable' && !isOn) {
      try {
        await Meteor.callPromise('opsCommand/importOpenTable', restaurantId);
        toggleConnectedSource(id);
        await scopeConfig.mutate();
        await scopeCatalog.mutate();
      } catch (e) {
        const err = e as { reason?: string; message?: string };
        window.alert(
          err.reason ??
            err.message ??
            i18n.t('errors:openTableImportFailed'),
        );
      }
      return;
    }

    if (isMarketingSourceId(id) && !isOn) {
      setMarketingWizardSource(id);
      return;
    }

    if (serverConnected.includes(id)) {
      toggleConnectedSource(id);
      return;
    }
    handleConnect(id);
  };


  const focusFromDrill = useMemo(() => {
    if (!drillDown.data || !focusOutlet) return focusOutlet;
    return focusOutlet;
  }, [drillDown.data, focusOutlet]);

  const situationOutletCount = scopeMembershipCount;

  const agentProfile = useMemo(
    () =>
      buildUserSituationProfile({
        mode,
        outletCount: situationOutletCount,
        hasNorthStar: !!northStar.data,
        perfSettings,
      }),
    [mode, situationOutletCount, northStar.data, perfSettings],
  );

  const enrichedSituationPack = useMemo(() => {
    if (mode !== 'performance') return situationPack.data;
    // Pack cards replace pattern nuggets in the brief; nuggets stay on their
    // own hero strip when agent patterns are off.
    const withNs = enrichSituationPack(situationPack.data, {
      northStar: northStar.data,
    });
    const diagnosing =
      Boolean(situationPack.data) &&
      (situationDiagnosis.isLoading ||
        (situationDiagnosis.data == null && situationDiagnosis.isValidating));
    const diagnosisFailed =
      Boolean(situationPack.data) &&
      !diagnosing &&
      Boolean(situationDiagnosis.error) &&
      situationDiagnosis.data == null;
    return mergeDiagnosisIntoPack(withNs, situationDiagnosis.data, {
      diagnosing,
      diagnosisFailed,
    });
  }, [
    mode,
    situationPack.data,
    northStar.data,
    situationDiagnosis.data,
    situationDiagnosis.error,
    situationDiagnosis.isLoading,
    situationDiagnosis.isValidating,
  ]);

  const openAgentSurface = () => {
    if (
      typeof window !== 'undefined' &&
      window.matchMedia(ASK_NARROW_QUERY).matches
    ) {
      setAgentOpen(true);
      return;
    }
    setAgentLaneOpen(true);
  };

  const handleAgentNavigate = (target: AgentNavigateTarget) => {
    const kind = target.kind;
    const navMode =
      target.mode === 'live' ||
      target.mode === 'performance' ||
      target.mode === 'settings' ||
      target.mode === 'horizon'
        ? target.mode
        : null;
    if (navMode) {
      setMode(navMode);
    } else if (kind === 'liveRecovery') {
      setMode('live');
    } else if (kind === 'settingsFinding') {
      setMode('settings');
    } else if (kind === 'horizonDate') {
      setMode('horizon');
    } else if (
      kind === 'drill' ||
      kind === 'outlet' ||
      kind === 'northStar' ||
      kind === 'metric'
    ) {
      setMode('performance');
    }

    if (kind === 'horizonDate' || target.mode === 'horizon') {
      if (target.serviceDate) setHorizonDate(target.serviceDate);
      if (target.outletId) setHorizonOutlet(target.outletId);
      if (kind === 'horizonDate' || !target.metricKey) return;
    }

    if (kind === 'northStar' || target.northStar) {
      setNorthStarDrill(true);
      return;
    }
    if (kind === 'drill' && target.metricKey) {
      setPerfDrillReturn(false);
      setPerfDrill(openPerfDrill(target.metricKey, target.outletId));
      return;
    }
    if (kind === 'outlet' && target.outletId) {
      const liveTarget = target.mode === 'live';
      if (liveTarget) {
        focusLiveOutlet(target.outletId);
      } else {
        const row = performance.data?.outletRows?.find(
          r => r.outlet.id === target.outletId,
        );
        if (row) setPerfOutlet(row);
        else {
          const o = performance.data?.outlets?.find(x => x.id === target.outletId);
          if (o) setFocusOutlet(o);
        }
      }
      return;
    }
    if (kind === 'liveRecovery') {
      setDrillKey(null);
      return;
    }
    if (kind === 'reservation' && target.outletId) {
      setMode('live');
      focusLiveOutlet(target.outletId);
    }
  };

  const handleSituationAct = (
    evidence: SituationEvidence,
    card: SituationCard,
  ) => {
    addCareAbout(card.insight.slice(0, 80));
    if (evidence.kind === 'metric') {
      handleAgentNavigate({
        kind: 'drill',
        mode: 'performance',
        metricKey: evidence.metricKey,
        outletId: card.outletId,
      });
      return;
    }
    if (evidence.kind === 'drill') {
      handleAgentNavigate({
        kind: 'drill',
        mode: 'performance',
        metricKey: evidence.metricKey,
        outletId: evidence.outletId ?? card.outletId,
      });
      return;
    }
    if (evidence.kind === 'outlet') {
      handleAgentNavigate({
        kind: 'outlet',
        mode: card.mode,
        outletId: evidence.outletId,
      });
      return;
    }
    if (evidence.kind === 'northStar') {
      handleAgentNavigate({ kind: 'northStar', northStar: true });
      return;
    }
    if (evidence.kind === 'liveRecovery') {
      handleAgentNavigate({ kind: 'liveRecovery', mode: 'live' });
      return;
    }
    if (evidence.kind === 'reservation') {
      handleAgentNavigate({
        kind: 'reservation',
        mode: 'live',
        reservationId: evidence.reservationId,
        outletId: evidence.outletId,
      });
      return;
    }
    if (evidence.kind === 'settingsFinding') {
      handleAgentNavigate({
        kind: 'settingsFinding',
        mode: 'settings',
        findingId: evidence.findingId,
      });
      return;
    }
    if (evidence.kind === 'mode') {
      setMode(evidence.mode);
    }
  };

  // Collapse returns to the screen the operator expanded from.
  const previousModeRef = useRef<typeof mode>(mode === 'assistant' ? 'performance' : mode);
  useEffect(() => {
    if (mode !== 'assistant') previousModeRef.current = mode;
  }, [mode]);

  // One maître d'AI for every screen: threads, the in-flight answer and
  // "Ask about this" live here so the panel and the home share them (docs/18).
  // In Growth the assistant reads the context's venues like every screen
  // (decision 7.7.1), with Growth's figures and windows as its context.
  // The demo group is fictional: maître d'AI never takes its venues as scope,
  // and while it is on screen the panel says it isn't available instead of
  // answering about the person's own venues.
  const growthAgent = mode === 'growth' && !growthDemo ? growthAgentScope : null;
  const agentDemoNotice = maitreDemoNotice(mode, growthDemo);
  const maitre = useMaitreController({
    restaurantId,
    regionFilter: regionArg,
    scopeKey: regionArg ?? 'all',
    scopeLabel: scopeLabel ?? 'This restaurant',
    source: source as string[],
    shifts,
    status: status as string[],
    perfSettings,
    viewContext: maitreContext,
    performance: performance.data ?? null,
    screen: mode,
    surface: mode === 'assistant' ? 'expanded' : 'panel',
    // Growth's figures and windows, so a Growth question is answered about
    // what Growth shows (WP4.5).
    growth: mode === 'growth' ? growthAgent?.context ?? null : null,
    existingNorthStar: northStar.data ?? null,
    openPanel: () => {
      if (mode !== 'assistant') openAgentSurface();
    },
    closePanel: () => {
      setAgentOpen(false);
      setAgentLaneOpen(false);
    },
    expand: () => {
      setAssistantPane('notebook');
      setMode('assistant');
    },
    collapse: () => {
      setMode(previousModeRef.current);
      openAgentSurface();
    },
    onNavigate: handleAgentNavigate,
    onNorthStarSaved: () => {
      setAgentOpen(false);
      if (mode !== 'live' && mode !== 'performance') {
        setMode('performance');
      }
      void northStar.mutate();
    },
    onNorthStarDeleted: () => {
      setAgentOpen(false);
      setNorthStarDrill(false);
      void northStar.mutate();
    },
  });

  // Every inline "Ask maître d'AI" opens a new chat. When it is about one
  // thing (a highlight, a forecast day, a channel) that thing is the focus.
  const askAgent = (prompt: string, focus?: AgentFocus) => {
    if (agentDemoNotice) {
      openAgentSurface();
      return;
    }
    // Store a short topic only — never the full “Tell me more…” prompt as a chip.
    addCareAbout(focus?.label ?? prompt);
    if (focus) maitre.askAbout(focus, prompt);
    else maitre.askNew(prompt);
  };

  const maitreWidgetData = {
    restaurantId,
    regionFilter: regionArg,
    perfSettings,
    source,
    status,
    shifts,
    connected,
  };

  const maitrePanelProps = {
    widgetData: maitreWidgetData,
    unavailable: agentDemoNotice,
    suggestions: agentProfile.chips,
    // On "Your Google accounts" the conversation row says which context it
    // uses; the header names the page, not Growth.
    lastRestaurantView: onGoogleAccounts,
    contextLabel: onGoogleAccounts
      ? i18n.t('growth:accountConnect.pageTitle')
      : mode === 'performance'
        ? 'Performance'
        : mode === 'live'
          ? 'Live'
          : mode === 'horizon'
            ? 'Forecast'
            : mode === 'growth'
              ? 'Growth'
              : mode === 'hotel'
                ? 'Hotel'
                : 'Settings',
  };

  // Cold load, or scope switch while keepPreviousData still shows the prior board.
  // Background 60s polls keep the same args key → no loader flash.
  const liveReady = !!live.data && !!kpis;
  const liveScopePending = liveSettledArgsKey !== liveArgsKey;
  const liveLoading = mode === 'live' && (!liveReady || liveScopePending);

  // Cancelling a load: the last view that finished loading (restaurant plus
  // venues, period, comparison and filters) is kept, so the loader can put it
  // back. Its data is still cached, so the previous screen returns at once;
  // the cancelled request finishes in the background and is cached too.
  const perfLoading = mode === 'performance' && !isComparing && performance.isLoading;
  const settledViewRef = useRef<{
    mode: typeof mode;
    restaurantId: string;
    state: typeof viewCtx.state;
  } | null>(null);
  const settled =
    !isComparing &&
    ((mode === 'live' && !liveLoading) ||
      (mode === 'performance' && isCurrentSWRData(performance)));
  if (settled) {
    settledViewRef.current = { mode, restaurantId, state: viewCtx.state };
  }
  const settledView = settledViewRef.current;
  const canCancelLoad =
    (liveLoading || perfLoading) &&
    !!settledView &&
    settledView.mode === mode &&
    (settledView.restaurantId !== restaurantId
      ? !!onSwitchRestaurant
      : JSON.stringify(settledView.state) !== JSON.stringify(viewCtx.state));
  // Another restaurant is opened by App first; its venues and filters follow
  // once it is on screen, so they are saved with that restaurant.
  const [pendingRestore, setPendingRestore] = useState<{
    restaurantId: string;
    state: typeof viewCtx.state;
  } | null>(null);
  const applyViewState = (state: typeof viewCtx.state) => {
    if (state.regionFilter !== regionFilter) setRegionFilter(state.regionFilter);
    if (JSON.stringify(state.perfSettings) !== JSON.stringify(perfSettings)) {
      setPerfSettings(state.perfSettings);
    }
    if (JSON.stringify(state.status) !== JSON.stringify(status)) setStatus(state.status);
    if (JSON.stringify(state.source) !== JSON.stringify(source)) setSource(state.source);
    if (JSON.stringify(state.shifts) !== JSON.stringify(shifts)) setShifts(state.shifts);
  };
  useEffect(() => {
    if (!pendingRestore || pendingRestore.restaurantId !== restaurantId) return;
    setPendingRestore(null);
    applyViewState(pendingRestore.state);
  }, [pendingRestore, restaurantId]);
  const cancelLoad = () => {
    const prev = settledViewRef.current;
    if (!prev) return;
    if (prev.restaurantId !== restaurantId) {
      if (!onSwitchRestaurant) return;
      setPendingRestore({ restaurantId: prev.restaurantId, state: prev.state });
      onSwitchRestaurant(prev.restaurantId);
      return;
    }
    applyViewState(prev.state);
  };

  const belowTopbarDrillOpen =
    !isComparing &&
    ((mode === 'performance' && !!perfDrill) ||
      northStarDrill ||
      (mode === 'performance' && reviewsDrillOpen));
  const agentLaneDocked = agentLaneOpen && belowTopbarDrillOpen;

  const ftueSurfaceCtx = useMemo<FtueSurfaceContext>(
    () => ({
      mode:
        mode === 'growth' || mode === 'assistant' || mode === 'hotel' ? 'performance' : mode,
      assistantOpen: mode === 'assistant',
      isComparing,
      perfDrillOpen: !!perfDrill,
      liveDrillOpen: mode === 'live' && !!drillKey,
      northStarDrillOpen: northStarDrill,
      reviewsDrillOpen,
      agentOpen: showAskLane || showAskDrawer,
      integrationsOpen,
      scopeSettingsOpen,
      outletDrawerOpen: !!perfOutlet || !!outletDrill,
      focusDrawerOpen: !!focusOutlet,
      reservationDrawerOpen,
      posConnectOpen,
      marketingWizardOpen: !!marketingWizardSource,
      marketingDrillOpen: false,
    }),
    [
      mode,
      isComparing,
      perfDrill,
      drillKey,
      northStarDrill,
      reviewsDrillOpen,
      showAskLane,
      showAskDrawer,
      integrationsOpen,
      scopeSettingsOpen,
      perfOutlet,
      outletDrill,
      focusOutlet,
      reservationDrawerOpen,
      posConnectOpen,
      marketingWizardSource,
    ],
  );

  const ftueNav = useMemo<FtueNavigateApi>(
    () => ({
      setMode: setModeTransient,
      restoreMode: setMode,
      openPerfDrill: (metricKey: string) => {
        setPerfDrillReturn(false);
        setPerfDrill(openPerfDrill(metricKey));
      },
      closePerfDrill: () => {
        setOutletDrill(null);
        setPerfDrill(null);
      },
      openAgent: () => openAgentSurface(),
      closeAgent: () => {
        setAgentOpen(false);
        setAgentLaneOpen(false);
      },
      closeOverlays: () => {
        setIntegrationsOpen(false);
        setScopeSettingsOpen(false);
        setPosConnectOpen(false);
        setMarketingWizardSource(null);
        setFocusOutlet(null);
        setPerfOutlet(null);
        setOutletDrill(null);
        setDrillKey(null);
        setPerfDrill(null);
        setNorthStarDrill(false);
        setReviewsDrillOpen(false);
        setAgentOpen(false);
        setAgentLaneOpen(false);
      },
      clearComparison: () => {
        setCompareItems([]);
      },
      captureSnapshot: (): FtueUiSnapshot => ({
        mode:
        mode === 'growth' || mode === 'assistant' || mode === 'hotel' ? 'performance' : mode,
        drillKey,
        perfDrill: perfDrill
          ? { metricKey: perfDrill.metricKey, outletId: perfDrill.outletId ?? undefined }
          : null,
        northStarDrill,
        reviewsDrillOpen,
        agentOpen,
        agentLaneOpen,
        outletDrill,
        focusOutletId: focusOutlet?.id ?? null,
        perfOutletId: perfOutlet?.outlet.id ?? null,
        integrationsOpen,
        scopeSettingsOpen,
        marketingDrillOpen: false,
        marketingWizardSource,
        posConnectOpen,
        compareItems: compareItems.map(i => ({
          filter: i.filter,
          label: i.label,
          color: i.color,
          dataSource: i.dataSource,
        })),
      }),
      restoreSnapshot: (snap: FtueUiSnapshot) => {
        setMode(snap.mode);
        setCompareItems(
          (snap.compareItems ?? []).map(i => ({
            filter: i.filter,
            label: i.label,
            color: i.color,
            dataSource: i.dataSource as ComparisonItem['dataSource'],
          })),
        );
        setDrillKey(snap.drillKey);
        setPerfDrill(
          snap.perfDrill
            ? openPerfDrill(snap.perfDrill.metricKey, snap.perfDrill.outletId)
            : null,
        );
        setNorthStarDrill(snap.northStarDrill);
        setReviewsDrillOpen(snap.reviewsDrillOpen);
        setAgentOpen(snap.agentOpen);
        setAgentLaneOpen(snap.agentLaneOpen);
        setOutletDrill(snap.outletDrill);
        setIntegrationsOpen(snap.integrationsOpen);
        setScopeSettingsOpen(snap.scopeSettingsOpen);
        setMarketingWizardSource(
          (snap.marketingWizardSource as ConnectedSourceId | null) ?? null,
        );
        setPosConnectOpen(snap.posConnectOpen);
        if (snap.focusOutletId) {
          const o = outlets.find(x => x.id === snap.focusOutletId) ?? null;
          setFocusOutlet(o);
        } else {
          setFocusOutlet(null);
        }
        if (snap.perfOutletId) {
          const row =
            performance.data?.outletRows?.find(
              r => r.outlet.id === snap.perfOutletId,
            ) ?? null;
          setPerfOutlet(row);
        } else {
          setPerfOutlet(null);
        }
        if (!snap.outletDrill) setOutletDrill(null);
        if (!snap.agentOpen) setAgentOpen(false);
        if (!snap.agentLaneOpen) setAgentLaneOpen(false);
      },
    }),
    [
      mode,
      drillKey,
      perfDrill,
      northStarDrill,
      reviewsDrillOpen,
      agentOpen,
      agentLaneOpen,
      outletDrill,
      focusOutlet,
      perfOutlet,
      integrationsOpen,
      scopeSettingsOpen,
      marketingWizardSource,
      posConnectOpen,
      compareItems,
      outlets,
      performance.data,
      setMode,
      setModeTransient,
      openAgentSurface,
    ],
  );

  const ftueReady =
    mode === 'live'
      ? liveReady && !liveLoading
      : mode === 'settings'
        ? !!settingsAudit.data || !settingsAudit.isLoading
        : !!performance.data || !performance.isLoading;

  const ftue = useFtueController({
    ready: ftueReady,
    surfaceCtx: ftueSurfaceCtx,
    nav: ftueNav,
  });

  // Live failed before painting anything (e.g. 503 "live service unavailable"
  // in a local run without an aleno token): said inline in the canvas, so the
  // navigation and the context bar stay and every other screen still opens.
  // A later poll error must not tear down the painted board, and an
  // AbortError is no failure (superseded fetch / Strict Mode remount).
  const liveFailed =
    mode === 'live' && !!live.error && !live.data && !isMethodAbortError(live.error);

  return (
    <ViewContextProvider value={viewContextValue}>
    <MaitreProvider value={maitre}>
    <div
      ref={opsRootRef}
      className={`ops-command${agentLaneDocked ? ' agent-lane-docked' : ''}${
        navExpanded ? ' nav-expanded' : ' nav-collapsed'
      }`}
      data-theme="light"
    >
      <div ref={setFtueAppRoot} className="ftue-app-root">
        <div className="shell">
        <NavRail
          mode={mode}
          setMode={setMode}
          connected={connected}
          onOpenIntegrations={handleOpenIntegrations}
          onOpenHelp={ftue.openHelp}
          expanded={navExpanded}
          collapseLocked={navForceCollapsed || agentSurfaceOpen}
          collapseLockedReason={
            agentSurfaceOpen
              ? `Collapsed while ${INSIGHTS_AGENT_NAME} is open`
              : 'Expand when the window is wider'
          }
          onToggleExpanded={() => setNavPrefExpanded(v => !v)}
          growthSections={growthNav.sections}
          growthSection={mode === 'growth' ? growthView : null}
          growthAttention={growthNav.attention}
          onGrowthSection={section => {
            setMode('growth');
            setGrowthView(section);
          }}
          hotelAvailable={hotelAvailable}
        />
        <div className="shell-main">
        <Topbar
          mode={mode}
          screenToolsRef={mode === 'growth' ? setGrowthToolsSlot : undefined}
          screen={screen}
          perfSettings={perfSettings}
          onChangePerfSettings={setPerfSettings}
          scopeGroups={visibleScopeGroups}
          regionFilter={regionFilter}
          setRegionFilter={setRegionFilter}
          onOpenScopeSettings={() => setScopeSettingsOpen(true)}
          outletCount={topbarOutletCount}
          showScopeSettings={showScopeSettings}
          venueIds={topbarScopeIds}
          demoVenues={
            mode === 'growth' && growthDemo
              ? {
                  label: growthDemoChoices?.group ?? growthAgentScope?.label ?? i18n.t('chrome:context.venues.demo'),
                  names: growthDemoChoices?.venues.map(v => v.name) ?? [],
                  focus: growthDemoChoices
                    ? {
                        value: growthDemoFocus,
                        onChange: setGrowthDemoFocus,
                        options: [
                          {
                            id: GROWTH_FLEET_PROPERTY,
                            label: i18n.t('chrome:context.focus.demoAll', {
                              group: growthDemoChoices.group,
                              count: growthDemoChoices.venues.length,
                            }),
                          },
                          ...growthDemoChoices.brands.map(b => ({
                            id: b.key,
                            label: i18n.t('chrome:context.focus.demoBrand', { name: b.name, count: b.count }),
                            group: 'brand' as const,
                          })),
                          ...growthDemoChoices.venues.map(v => ({ id: v.id, label: v.name, group: 'venue' as const })),
                        ],
                      }
                    : null,
                }
              : null
          }
          ownWindow={ownWindow}
          focus={focusCrumbs}
          link={
            viewCtx.origin === 'link'
              ? { facts: venuesLink, onKeep: viewCtx.keepLinkContext, onBack: viewCtx.restoreSavedContext }
              : null
          }
          fleetRestaurants={outletCatalog}
          groupName={currentRestaurantName}
          accessibleRestaurants={accessibleRestaurantList}
          onSwitchRestaurant={onSwitchRestaurant}
          activeRestaurantId={restaurantId}
          restaurantId={restaurantId}
          scopeGroupConfigs={scopeConfig.data?.groups ?? []}
          onScopeGroupsChanged={async () => {
            await scopeConfig.mutate();
          }}
          orgVenues={orgVenues}
          compareItems={compareItems}
          onCompareChange={setCompareItems}
          source={source}
          onChangeSource={setSource}
          status={status}
          onChangeStatus={setStatus}
          shifts={shifts}
          onChangeShifts={setShifts}
          availableShifts={availableShifts}
          onOpenAsk={openAgentSurface}
          agentBusy={agentBusy}
        />

        {connectToast
          ? createPortal(
              <div className="ops-snackbar-host">
                <div className="ops-toast" role="status" aria-live="polite">
                  <Icon name="info" size={16} className="ops-toast-ic" />
                  <span className="ops-toast-msg">{connectToast}</span>
                </div>
              </div>,
              document.body,
            )
          : null}
        {mode === 'live' && bookingToast
          ? createPortal(
              <div className="ops-snackbar-host">
                <div className="ops-toast" role="status" aria-live="polite">
                  <Icon name="check" size={16} className="ops-toast-ic" />
                  <span className="ops-toast-msg">{bookingToast}</span>
                </div>
              </div>,
              document.body,
            )
          : null}
        {mode === 'live' && live.data?.truncated
          ? createPortal(
              <div className="ops-snackbar-host">
                <div className="ops-toast" role="status" aria-live="polite">
                  <Icon name="info" size={16} className="ops-toast-ic" />
                  <span className="ops-toast-msg">
                    Live board capped at 1,000 reservations for{' '}
                    {(live.data.truncatedRestaurantIds?.length ?? 0) || 'some'}{' '}
                    outlet
                    {(live.data.truncatedRestaurantIds?.length ?? 0) === 1
                      ? ''
                      : 's'}{' '}
                    — counts may be incomplete.
                  </span>
                </div>
              </div>,
              document.body,
            )
          : null}
        {agentToast
          ? createPortal(
              <div className="ops-snackbar-host">
                <button
                  type="button"
                  className="ops-toast ops-toast-action"
                  onClick={openAgentToastTarget}
                >
                  <Icon name="check" size={16} className="ops-toast-ic" />
                  <span className="ops-toast-msg">
                    {INSIGHTS_AGENT_NAME} finished — tap to view answer
                  </span>
                </button>
              </div>,
              document.body,
            )
          : null}

        <div className={`main${showAskLane ? ' with-rail' : ''}`}>
          <main className="canvas">
            {isComparing && mode === 'live' ? (
              <ComparisonLiveView
                items={comparison.items}
                loading={comparison.loading}
              />
            ) : isComparing && mode === 'performance' ? (
              <ComparisonPerfView
                items={comparison.items}
                loading={comparison.loading}
              />
            ) : isComparing && mode === 'settings' ? (
              comparison.loading ? (
                <OpsSkeleton />
              ) : (
                <div data-ftue="comparison-view">
                  <SettingsHealthPanel
                    audit={comparison.settingsAudit ?? undefined}
                  />
                </div>
              )
            ) : mode === 'live' ? (
              liveFailed ? (
                <LiveUnavailable
                  message={live.error?.message ?? ''}
                  onRetry={() => void live.mutate()}
                />
              ) : liveLoading ? (
                <PerformanceLoader
                  label="Reading tonight's service"
                  onCancel={canCancelLoad ? cancelLoad : undefined}
                />
              ) : (
              <>
                <ServiceRibbon
                  kpis={kpis!}
                  forecast={forecastData}
                  connected={connected}
                  benchmarks={live.data?.timelineBenchmarks ?? null}
                  serviceTimeline={live.data?.serviceTimeline ?? null}
                  kitchenBriefing={live.data?.kitchenBriefing ?? null}
                  liveOutletId={liveOutletId}
                  onLiveOutletIdChange={id => {
                    if (id) focusLiveOutlet(id);
                  }}
                  onReservationDrawerVisibilityChange={
                    setReservationDrawerOpen
                  }
                  pickerOutlets={livePickerOutlets}
                  outlets={outlets.map(o => ({
                    id: o.id,
                    name: o.name,
                    seated: o.seated,
                    closed: o.closed,
                    city: o.city,
                    region: o.region,
                    seats: o.seats,
                    tables: o.tables,
                    occTables: o.occTables,
                    coversToday: o.coversToday,
                    upcoming: o.upcoming,
                    bookedAhead: o.bookedAhead,
                    avgSeatMin: o.avgSeatMin,
                    noShowCovers: o.noShowCovers,
                    cancelCovers: o.cancelCovers,
                    gapCovers: o.gapCovers,
                    lostGapCovers: o.lostGapCovers,
                    spendCover: o.spendCover,
                    noShow: o.noShow,
                    cancelRate: o.cancelRate,
                    sellRevenue: o.sellRevenue,
                    sellCount: o.sellCount,
                    sellAttach: o.sellAttach,
                  }))}
                  onDrill={setDrillKey}
                />

                {/* Act on gaps the floor can still recover. */}
                <div className="act-head">Act on it</div>
                <RecoverySection
                  recovery={liveRecovery}
                  onFocus={id => {
                    focusLiveOutlet(id);
                    const o = outlets.find(x => x.id === id);
                    if (o) setFocusOutlet(o);
                  }}
                />

                {/* Supplementary signals. */}
                <div className="act-head">More signals</div>
                <SellItemsSection
                  outlets={liveOutlets}
                  connected={connected}
                  onDrill={() => setDrillKey('sellitems')}
                />
                <ExternalSignals
                  outlets={liveOutlets}
                  connected={connected}
                  onConnect={handleConnect}
                />
              </>
              )
            ) : mode === 'settings' ? (
              settingsAudit.isLoading && !settingsAudit.data ? (
                <OpsSkeleton />
              ) : (
                <>
                  <SettingsHealthPanel audit={settingsAudit.data} />
                  <SubscriptionUsagePanel usage={subscriptionUsage.data} />
                </>
              )
            ) : mode === 'horizon' ? (
              <React.Suspense fallback={<PerformanceLoader />}>
                <HorizonView
                  restaurantId={restaurantId}
                  regionFilter={regionFilter}
                  shifts={shifts}
                  selectedDate={horizonDate}
                  selectedOutletId={horizonOutlet}
                  onSelect={(serviceDate, outletId) => {
                    setHorizonDate(serviceDate);
                    setHorizonOutlet(outletId);
                  }}
                  windowKey={horizonWindow}
                  onWindowChange={setHorizonWindow}
                  onAsk={askAgent}
                  onOpenChannels={() => {
                    setMode('growth');
                    setGrowthView('sources');
                  }}
                />
              </React.Suspense>
            ) : mode === 'assistant' ? (
              <React.Suspense fallback={<PerformanceLoader />}>
                <MaitreHome
                  pane={assistantPane}
                  onPaneChange={setAssistantPane}
                  widgetData={maitreWidgetData}
                />
              </React.Suspense>
            ) : mode === 'hotel' ? (
              <React.Suspense fallback={<PerformanceLoader />}>
                <HotelView
                  restaurantId={restaurantId}
                  regionFilter={regionArg}
                  tab={hotelView}
                  onTabChange={setHotelView}
                  onConnectPos={() => handleConnect('pos')}
                />
              </React.Suspense>
            ) : mode === 'growth' ? (
              <React.Suspense fallback={<PerformanceLoader />}>
                <GrowthView
                  restaurantId={restaurantId}
                  regionFilter={regionFilter}
                  perfSettings={perfSettings}
                  subview={growthView}
                  onChangeSubview={setGrowthView}
                  openChannelId={growthChannel}
                  onChangeChannel={setGrowthChannel}
                  openDive={growthDive}
                  onChangeDive={dive => {
                    setGrowthDive(dive);
                    setGrowthFacet(null);
                  }}
                  openFacet={growthFacet}
                  onChangeFacet={setGrowthFacet}
                  setupSourceId={marketingWizardSource}
                  scopedRestaurantIds={topbarScopeIds}
                  onMappedRestaurants={expandMappedScope}
                  onSetupSource={setMarketingWizardSource}
                  onAsk={askAgent}
                  restaurantCatalog={outletCatalog}
                  venueLabel={growthDemo ? null : (scopeLabel ?? currentRestaurantName)}
                  filters={growthFilters}
                  model={growthModel}
                  onChangeModel={setGrowthModel}
                  demo={growthDemo}
                  demoFocus={growthDemoFocus}
                  onDemoChoices={setGrowthDemoChoices}
                  onChangeDemo={on => {
                    // Demo venue ids never stay selected outside the demo, nor real ones inside it.
                    setGrowthDemo(on);
                    setGrowthDemoFocus(GROWTH_FLEET_PROPERTY);
                    setGrowthModel(null);
                    setGrowthDive(null);
                    setGrowthFacet(null);
                    setGrowthChannel(null);
                    setGrowthView(on ? 'overview' : 'health');
                  }}
                  onAgentScope={setGrowthAgentScope}
                  onNavReport={growthNav.report}
                  toolsSlot={growthToolsSlot}
                  connected={connected}
                  onOpenForecast={(serviceDate, outletId) => {
                    setMode('horizon');
                    setHorizonDate(serviceDate);
                    setHorizonOutlet(outletId);
                  }}
                  onConnectPos={() => handleConnect('pos')}
                />
              </React.Suspense>
            ) : (
              <PerformanceView
                data={performance.data}
                connected={connected}
                onSelectOutlet={setFocusOutlet}
                onSelectPerfOutlet={(row, metricKey) => {
                  setPerfOutletMetric(metricKey ?? null);
                  setPerfOutlet(row);
                }}
                onDrill={(metricKey, outletId) => {
                  setPerfDrillReturn(false);
                  setPerfDrill(openPerfDrill(metricKey, outletId));
                }}
                loading={performance.isLoading}
                onCancelLoading={canCancelLoad ? cancelLoad : undefined}
                scopeLabel={scopeLabel}
                activeStatus={status}
                activeSource={source}
                guestReviews={guestReviews.data}
                guestReviewsLoading={guestReviews.isLoading}
                heroNuggets={performanceHeroNuggets.data?.heroNuggets}
                heroNuggetsLoading={
                  performanceHeroNuggets.data == null &&
                  (performanceHeroNuggets.isLoading ||
                    performanceHeroNuggets.isValidating)
                }
                onConnect={handleConnect}
                northStar={northStar.data}
                onNorthStarDrill={() => setNorthStarDrill(true)}
                onNorthStarEdit={maitre.startNorthStar}
                situationPack={enrichedSituationPack}
                situationLoading={
                  situationPack.isLoading ||
                  (situationPack.data == null && situationPack.isValidating)
                }
                situationProfile={agentProfile}
                onSituationAct={handleSituationAct}
                onSituationAsk={askAgent}
                restaurantId={restaurantId}
                onOpenLocalSearch={() => {
                  setMode('growth');
                  setGrowthView('local');
                }}
                events={eventsOverview.data}
                eventsLoading={eventsOverview.isLoading}
                onOpenEvent={name => {
                  // Every tile reads the event: the drill opens on the
                  // Guests metric with the shift filter set to it.
                  setShifts([name], ADJUST);
                  setPerfDrillReturn(false);
                  setPerfDrill(openPerfDrill('covers'));
                }}
              />
            )}
          </main>

          {showAskLane && (
            <aside className="agent-lane" aria-label={`${INSIGHTS_AGENT_NAME} lane`}>
              <MaitrePanel
                {...maitrePanelProps}
                variant="lane"
                onClose={() => setAgentLaneOpen(false)}
              />
            </aside>
          )}
        </div>
        </div>
      </div>

      {integrationsOpen && (
        <IntegrationsDrawer
          connected={connected}
          serverConnected={serverConnected}
          focusSourceId={focusIntegrationSource}
          onClose={handleCloseIntegrations}
          onToggle={handleToggleSource}
          onManageMarketing={id => {
            setIntegrationsOpen(false);
            setMode('growth');
            setGrowthView('setup');
            setMarketingWizardSource(id);
          }}
          onOpenMarketingAttribution={() => {
            setIntegrationsOpen(false);
            setMode('growth');
            setGrowthView('sources');
          }}
          onOpenGoogleAccounts={() => {
            setIntegrationsOpen(false);
            setMode('growth');
            setGrowthView('accounts');
          }}
          onOpenReviews={
            connected.includes('reviews')
              ? () => {
                  setIntegrationsOpen(false);
                  setMode('performance');
                  setPerfDrill(null);
                  setReviewsDrillOpen(true);
                }
              : undefined
          }
          onRequestPosConnect={() => {
            setIntegrationsOpen(false);
            setPosConnectOpen(true);
          }}
        />
      )}

      {posConnectOpen && (
        <PosConnectModal
          multiOutlet={scopeMembershipCount > 1}
          onClose={() => setPosConnectOpen(false)}
        />
      )}

      {marketingWizardSource && mode !== 'growth' && (
        <MarketingConnectWizard
          sourceId={marketingWizardSource}
          restaurantId={restaurantId}
          scopedRestaurantIds={topbarScopeIds}
          onMappedRestaurants={expandMappedScope}
          onClose={() => setMarketingWizardSource(null)}
          onConnected={async () => {
            await scopeCatalog.mutate();
            setMarketingWizardSource(null);
            setMode('growth');
            setGrowthView('website');
          }}
        />
      )}

      {showAskDrawer && (
        <MaitrePanel
          {...maitrePanelProps}
          variant="drawer"
          onClose={() => setAgentOpen(false)}
        />
      )}

      {northStarDrill && !isComparing && (
        (northStarDrillDown.data?.northStar ?? northStar.data) && northStarDrillDown.data ? (
          <NorthStarDrillDown
            // The drill's own evaluation: same window as its weak points and plan.
            payload={(northStarDrillDown.data.northStar ?? northStar.data)!}
            panels={northStarDrillDown.data.panels ?? []}
            settings={perfSettings}
            window={northStarDrillDown.data.window}
            scopeLabel={
              scopeLabel
                ? i18n.t('performance:outletScope.withCount', { scope: scopeLabel, count: scopeMembershipCount })
                : i18n.t('chrome:filter.allOutlets')
            }
            bucketLabels={performance.data?.series?.map(b => b.label)}
            onClose={() => setNorthStarDrill(false)}
            onNavigate={handleNorthStarNav}
          />
        ) : northStarDrillDown.isLoading || northStar.isLoading ? (
          <DrillLoadingOverlay
            onClose={() => setNorthStarDrill(false)}
            belowTopbar
            activeMetric="northstar"
          />
        ) : null
      )}

      {scopeSettingsOpen && (
        <ScopeGroupsModal
          restaurantId={restaurantId}
          onClose={() => setScopeSettingsOpen(false)}
          onSaved={async () => {
            setCompareItems([]);
            await Promise.all([
              scopeCatalog.mutate(),
              scopedCount.mutate(),
              scopeConfig.mutate(),
              live.mutate(),
            ]);
          }}
        />
      )}

      {focusFromDrill && (
        <FocusDrawer
          outlet={focusFromDrill}
          connected={connected}
          onClose={() => setFocusOutlet(null)}
        />
      )}

      {mode === 'performance' && perfOutlet && !outletDrill && !isComparing && performance.data && (
        <OutletDeckDrawer
          key={perfOutlet.outlet.id}
          restaurantId={restaurantId}
          settings={perfSettings}
          regionFilter={regionArg}
          source={source}
          status={status}
          shifts={shifts}
          outletId={perfOutlet.outlet.id}
          outletName={perfOutlet.outlet.name}
          outletMeta={[perfOutlet.outlet.region, perfOutlet.outlet.city].filter(Boolean).join(' · ')}
          initialMetricKey={perfOutletMetric ?? activePerfTab?.key}
          periodLabel={performance.data.periodLabel}
          connected={connected}
          bucketLabels={performance.data.series.map(b => b.label)}
          aggregationLabel={performance.data.aggregationLabel}
          comparable={performance.data.comparable ?? false}
          onClose={() => setPerfOutlet(null)}
        />
      )}

      {mode === 'live' && drillKey && !isComparing && (
        drillDown.data && kpis ? (
          <DrillDown
            data={drillDown.data}
            outlets={outlets}
            kpis={kpis}
            connected={connected}
            scopeLabel={
              scopeLabel
                ? i18n.t('performance:outletScope.withCount', { scope: scopeLabel, count: scopeMembershipCount })
                : i18n.t('performance:outletScope.withCount', { scope: i18n.t('chrome:filter.allOutlets'), count: scopeMembershipCount })
            }
            onClose={() => setDrillKey(null)}
            onSelectOutlet={id => {
              const o = outlets.find(x => x.id === id);
              if (o) setFocusOutlet(o);
            }}
          />
        ) : drillDown.isLoading ? (
          <DrillLoadingOverlay onClose={() => setDrillKey(null)} />
        ) : null
      )}

      {mode === 'performance' && perfDrill && !isComparing && (
        perfDrillReady && perfDrillDown.data ? (
          <PerformanceDrillDown
            key={perfDrillDown.data.key}
            data={perfDrillDown.data}
            periodLabel={performance.data?.periodLabel ?? ''}
            scopeLabel={
              scopeLabel
                ? i18n.t('performance:outletScope.withCount', { scope: scopeLabel, count: scopeMembershipCount })
                : i18n.t('chrome:filter.allOutlets')
            }
            onClose={() => {
              setOutletDrill(null);
              setPerfDrill(null);
              if (perfDrillReturn) {
                returnToNorthStar();
              }
            }}
            onSwitchMetric={(tab: DrillTabDef) =>
              setPerfDrill({
                metricKey: tab.key,
                outletId: perfDrill.outletId,
              })
            }
            onSelectOutlet={outletId => {
              const row = perfDrillDown.data?.outlets.find(o => o.id === outletId);
              const outlet = outlets.find(o => o.id === outletId);
              setOutletDrill({
                outletId,
                name: row?.name ?? outlet?.name ?? outletId,
                meta: outlet
                  ? `${outlet.region}${outlet.city ? ` · ${outlet.city}` : ''}`
                  : undefined,
              });
            }}
            onOpenReviews={() => {
              setPerfDrill(null);
              setReviewsDrillOpen(true);
            }}
            reviewsAvailable={perfDrillReviewsAvailable}
            sourceBreakdown={performance.data?.sourceBreakdown}
            connected={connected}
            bucketLabels={performance.data?.series?.map(b => b.label)}
            aggregationLabel={performance.data?.aggregationLabel}
            comparable={performance.data?.comparable ?? false}
            scrollToSection={perfDrill.scrollTo}
            opportunityNarrative={
              opportunityForMetric
                ? (opportunityNarrative.data?.narrative ?? null)
                : null
            }
            opportunityNarrativeLoading={opportunityNarrativeLoading}
            agentCtx={{
              metricKey: perfDrill.metricKey,
              ...(perfDrill.outletId ? { outletId: perfDrill.outletId } : {}),
            }}
            deckSuspended={!!outletDrill}
          />
        ) : perfDrillPending ? (
          <DrillLoadingOverlay
            onClose={() => {
              clearDrillFocus();
              setOutletDrill(null);
              setPerfDrill(null);
              if (perfDrillReturn) {
                returnToNorthStar();
              }
            }}
            belowTopbar
            periodLabel={performance.data?.periodLabel}
            activeMetric={perfDrill.metricKey}
            reviewsAvailable={perfDrillReviewsAvailable}
            connected={connected}
            data-ftue="perf-drill"
            onSwitchMetric={(tab: DrillTabDef) =>
              setPerfDrill({
                metricKey: tab.key,
                outletId: perfDrill.outletId,
              })
            }
          />
        ) : perfDrillFailed ? (
          <DrillErrorOverlay
            onClose={() => {
              clearDrillFocus();
              setOutletDrill(null);
              setPerfDrill(null);
              if (perfDrillReturn) {
                returnToNorthStar();
              }
            }}
            onRetry={() => {
              void perfDrillDown.mutate();
            }}
            belowTopbar
            periodLabel={performance.data?.periodLabel}
            activeMetric={perfDrill.metricKey}
            reviewsAvailable={perfDrillReviewsAvailable}
            connected={connected}
            data-ftue="perf-drill"
            onSwitchMetric={(tab: DrillTabDef) =>
              setPerfDrill({
                metricKey: tab.key,
                outletId: perfDrill.outletId,
              })
            }
          />
        ) : null
      )}

      {mode === 'performance' && reviewsDrillOpen && !isComparing && (
        guestReviews.isLoading && !guestReviews.data ? (
          <DrillLoadingOverlay
            onClose={() => {
              setReviewsDrillOpen(false);
              if (perfDrillReturn) {
                returnToNorthStar();
              }
            }}
            belowTopbar
          />
        ) : guestReviews.data?.enabled ? (
          <GuestReviewsDrillDown
            data={guestReviews.data}
            scopeLabel={
              scopeLabel
                ? i18n.t('performance:outletScope.withCount', { scope: scopeLabel, count: scopeMembershipCount })
                : i18n.t('chrome:filter.allOutlets')
            }
            connected={connected}
            onClose={() => {
              setReviewsDrillOpen(false);
              if (perfDrillReturn) {
                returnToNorthStar();
              }
            }}
            onSwitchMetric={(tab: DrillTabDef) => {
              setReviewsDrillOpen(false);
              setPerfDrill({ metricKey: tab.key, outletId: null });
            }}
          />
        ) : null
      )}

      {mode === 'performance' &&
        outletDrill &&
        activePerfTab &&
        !isComparing && (
          <OutletDeckDrawer
            key={outletDrill.outletId}
            restaurantId={restaurantId}
            settings={perfSettings}
            regionFilter={regionArg}
            source={source}
            status={status}
            shifts={shifts}
            outletId={outletDrill.outletId}
            outletName={outletDrill.name}
            outletMeta={outletDrill.meta}
            initialMetricKey={activePerfPair?.primary ?? activePerfTab.key}
            periodLabel={performance.data?.periodLabel ?? ''}
            connected={connected}
            bucketLabels={performance.data?.series?.map(b => b.label)}
            aggregationLabel={performance.data?.aggregationLabel}
            comparable={performance.data?.comparable ?? false}
            onClose={() => setOutletDrill(null)}
          />
        )}

      </div>
      <div
        ref={setFtuePortalHost}
        className="ftue-portal-host"
        aria-live="off"
      />
      <FtueOverlay
        ftue={ftue}
        appRoot={ftueAppRoot}
        portalHost={ftuePortalHost}
        onAskStarter={prompt => {
          askAgent(prompt);
        }}
      />
    </div>
    </MaitreProvider>
    </ViewContextProvider>
  );
}
